import type {
  ContextUsage,
  PendingConfirmation,
  WorkflowTranscriptMessage
} from "@layout/GlobalAssistant/hooks/useIntentKitAgentChat";
import { Divider, IconButton, Stack, useTheme } from "@mui/material";
import BaseTooltip from "@ui/atoms/BaseTooltip/BaseTooltip";
import BaseTypography from "@ui/atoms/BaseTypography/BaseTypography";
import {
  KeyboardEvent,
  UIEvent,
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";
import ReactMarkdown from "react-markdown";
import { percent } from "@utils/metricFormat";

import { ConfirmationCard } from "./ConfirmationCard";
import {
  clampPercent,
  hasScrollShadow,
  SHADOW_RECHECK_DELAY_MS
} from "./aiAssistantChatShell.helpers";

import {
  AssistantChatRoot,
  ContextCircularProgress,
  ContextProgressPill,
  ContextProgressRing,
  ContextProgressRow,
  ContextProgressTrack,
  ComposerForm,
  ComposerTextField,
  ComposerToolbar,
  GreetingText,
  MessageBubble,
  MessageText,
  MessageWrapper,
  ProcessingStack,
  SendButton,
  ThinkingIndicator,
  ThinkingLogo,
  TranscriptMainContainer,
  TranscriptShadowContainer,
  TranscriptShadowOverlay,
  TranscriptStack
} from "./AIAssistantChatShell.components";
import Logo from "@ui/atoms/Logo/Logo";
import Icon from "@ui/atoms/Icon/Icon";

export type AIAssistantChatShellProps = {
  composerDisabled?: boolean;
  contextUsage?: ContextUsage;
  firstName?: string;
  isProcessing?: boolean;
  isAwaitingIntent?: boolean;
  messages: WorkflowTranscriptMessage[];
  onClearChat?: () => void;
  onConfirmAction?: () => void;
  onCancelAction?: () => void;
  pendingConfirmation?: PendingConfirmation;
  sendUserMessage: (text: string) => Promise<void>;
  statusLine: string;
  stopGenerating: () => void;
};

export const AIAssistantChatShell = ({
  composerDisabled = false,
  contextUsage,
  firstName,
  isAwaitingIntent = false,
  isProcessing = false,
  messages,
  onCancelAction,
  onClearChat,
  onConfirmAction,
  pendingConfirmation,
  sendUserMessage,
  statusLine,
  stopGenerating
}: AIAssistantChatShellProps): JSX.Element => {
  const theme = useTheme();
  const [draftInput, setDraftInput] = useState("");
  const [showShadow, setShowShadow] = useState(false);
  const transcriptScrollRef = useRef<HTMLDivElement>(null);

  const hasTranscript = !isAwaitingIntent;
  const contextPercent = clampPercent(contextUsage?.percentage ?? 0);
  const contextTooltip = contextUsage
    ? `Context used: ${percent(contextPercent)}`
    : "";

  const submit = useCallback(() => {
    const trimmed = draftInput.trim();

    if (!trimmed || composerDisabled || isProcessing) {
      return;
    }

    void sendUserMessage(trimmed);
    setDraftInput("");
  }, [composerDisabled, draftInput, isProcessing, sendUserMessage]);

  const handleSendOrStop = () =>
    isProcessing ? stopGenerating() : void submit();

  const onComposerKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void submit();
    }
  };

  const updateTranscriptShadow = useCallback(() => {
    const scrollEl = transcriptScrollRef.current;
    if (!scrollEl) {
      return;
    }
    setShowShadow(hasScrollShadow(scrollEl));
  }, []);

  const handleTranscriptScroll = (e: UIEvent<HTMLDivElement>) => {
    setShowShadow(hasScrollShadow(e.currentTarget));
  };

  useEffect(() => {
    const scrollEl = transcriptScrollRef.current;
    if (!scrollEl) {
      return;
    }

    scrollEl.scrollTo({ behavior: "smooth", top: scrollEl.scrollHeight });

    const frameId = requestAnimationFrame(updateTranscriptShadow);
    const timeoutId = window.setTimeout(
      updateTranscriptShadow,
      SHADOW_RECHECK_DELAY_MS
    );

    return () => {
      cancelAnimationFrame(frameId);
      window.clearTimeout(timeoutId);
    };
  }, [messages, statusLine, isProcessing, updateTranscriptShadow]);

  useEffect(() => {
    const scrollEl = transcriptScrollRef.current;
    if (!scrollEl) {
      return;
    }

    const observer = new ResizeObserver(() => {
      updateTranscriptShadow();
    });

    observer.observe(scrollEl);

    return () => observer.disconnect();
  }, [updateTranscriptShadow]);

  return (
    <AssistantChatRoot isAwaitingIntent={isAwaitingIntent} spacing={3}>
      {hasTranscript ? (
        <TranscriptMainContainer>
          <TranscriptShadowContainer>
            <TranscriptShadowOverlay showShadow={showShadow} />
          </TranscriptShadowContainer>

          <TranscriptStack
            ref={transcriptScrollRef}
            onScroll={handleTranscriptScroll}
          >
            {messages.map((message) => (
              <MessageWrapper key={message.id} isUser={message.role === "user"}>
                <MessageBubble isUser={message.role === "user"}>
                  <MessageText variant="body2">
                    {message.role === "user" ? (
                      <span style={{ whiteSpace: "pre-wrap" }}>
                        {message.text}
                      </span>
                    ) : (
                      <ReactMarkdown>{message.text}</ReactMarkdown>
                    )}
                  </MessageText>
                </MessageBubble>
              </MessageWrapper>
            ))}

            {statusLine && !isProcessing ? (
              <BaseTypography color="text.secondary" variant="caption">
                {statusLine}
              </BaseTypography>
            ) : null}
          </TranscriptStack>
        </TranscriptMainContainer>
      ) : (
        <Stack>
          <GreetingText>Hi {firstName},</GreetingText>
          <GreetingText>how can I help you?</GreetingText>
        </Stack>
      )}

      {isProcessing ? (
        <ProcessingStack>
          <ThinkingIndicator>
            <ThinkingLogo>
              <Logo
                variant="mark"
                width={22}
                startColor={theme.ui.logo.gradient.from}
                endColor={theme.ui.logo.gradient.to}
              />
            </ThinkingLogo>
            <BaseTypography
              variant="caption"
              color="text.secondary"
              text={statusLine || "Thinking..."}
            />
          </ThinkingIndicator>
        </ProcessingStack>
      ) : null}

      {pendingConfirmation ? (
        <ConfirmationCard
          body={pendingConfirmation.action.body}
          disabled={isProcessing}
          title={pendingConfirmation.action.title}
          onCancel={() => onCancelAction?.()}
          onConfirm={() => onConfirmAction?.()}
        />
      ) : null}

      <ComposerForm>
        <ComposerTextField
          fullWidth
          maxRows={6}
          minRows={3}
          multiline
          placeholder="Ask anything..."
          value={draftInput}
          variant="standard"
          slotProps={{
            htmlInput: { readOnly: composerDisabled },
            input: { disableUnderline: true }
          }}
          onChange={(e) => setDraftInput(e.target.value)}
          onKeyDown={onComposerKeyDown}
        />

        {hasTranscript ? (
          <ContextProgressRow>
            <BaseTooltip placement="top" title={contextTooltip}>
              <ContextProgressPill>
                <ContextProgressRing>
                  <ContextProgressTrack
                    size={20}
                    thickness={5}
                    value={100}
                    variant="determinate"
                  />
                  <ContextCircularProgress
                    color="primary"
                    size={20}
                    thickness={5}
                    value={Math.max(contextPercent, 1)}
                    variant="determinate"
                  />
                </ContextProgressRing>
              </ContextProgressPill>
            </BaseTooltip>
            {onClearChat ? (
              <BaseTooltip placement="top" title="Clear chat history">
                <IconButton
                  aria-label="Clear chat history"
                  size="small"
                  onClick={onClearChat}
                >
                  <Icon name="clear" size="sm" />
                </IconButton>
              </BaseTooltip>
            ) : null}
          </ContextProgressRow>
        ) : null}

        <Divider sx={{ margin: "0 16px" }} />

        <ComposerToolbar>
          <SendButton
            aria-label={isProcessing ? "Stop response" : "Send message"}
            disabled={
              isProcessing ? false : composerDisabled || !draftInput.trim()
            }
            onClick={handleSendOrStop}
          >
            {isProcessing ? (
              <Icon name="stop" size="xs" />
            ) : (
              <Icon name="chevron-up" />
            )}
          </SendButton>
        </ComposerToolbar>
      </ComposerForm>
    </AssistantChatRoot>
  );
};
